import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useSettings } from '../context/SettingsContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Upload, ArrowLeft, Loader2, Mail, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const { settings } = useSettings();

    const businessName = settings?.business_name || 'BGRemove';
    const logoUrl = settings?.logo_url;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email) {
            toast.error('Please enter your email');
            return;
        }

        setLoading(true);
        try {
            await axios.post(`${API}/auth/request-password-reset`, { email });
            setSubmitted(true);
            toast.success('Check your email for reset instructions');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to send reset email');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex">
            {/* Grain overlay */}
            <div className="grain-overlay" />

            {/* Left side - Brand */}
            <div className="hidden lg:flex lg:w-1/2 bg-foreground text-background p-12 flex-col justify-between">
                <Link to="/" className="flex items-center gap-2">
                    {logoUrl ? (
                        <img
                            src={logoUrl.startsWith('http') ? logoUrl : `${process.env.REACT_APP_BACKEND_URL}${logoUrl}`}
                            alt={businessName}
                            className="w-8 h-8 rounded-lg object-contain"
                        />
                    ) : (
                        <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                            <Upload className="w-4 h-4 text-primary-foreground" />
                        </div>
                    )}
                    <span className="font-heading font-bold text-xl tracking-tight">{businessName}</span>
                </Link>

                <div>
                    <h2 className="font-heading font-bold text-4xl tracking-tight mb-4">
                        Reset Password
                    </h2>
                    <p className="text-background/70 text-lg">
                        Enter your email and we'll send you instructions to reset your password.
                    </p>
                </div>

                <p className="text-sm text-background/50">
                    © 2024 {businessName}. All rights reserved.
                </p>
            </div>

            {/* Right side - Form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
                <div className="w-full max-w-md">
                    <Link
                        to="/login"
                        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to login
                    </Link>

                    <div className="lg:hidden mb-8">
                        <Link to="/" className="flex items-center gap-2">
                            {logoUrl ? (
                                <img
                                    src={logoUrl.startsWith('http') ? logoUrl : `${process.env.REACT_APP_BACKEND_URL}${logoUrl}`}
                                    alt={businessName}
                                    className="w-8 h-8 rounded-lg object-contain"
                                />
                            ) : (
                                <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                                    <Upload className="w-4 h-4 text-primary-foreground" />
                                </div>
                            )}
                            <span className="font-heading font-bold text-xl tracking-tight">{businessName}</span>
                        </Link>
                    </div>

                    {!submitted ? (
                        <>
                            <h1 className="font-heading font-bold text-3xl tracking-tight mb-2">
                                Forgot password?
                            </h1>
                            <p className="text-muted-foreground mb-8">
                                No worries, we'll send you reset instructions
                            </p>

                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="space-y-2">
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="you@example.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="h-12"
                                        autoFocus
                                    />
                                </div>

                                <Button
                                    type="submit"
                                    className="w-full h-12 rounded-full text-base"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <Mail className="mr-2 h-4 w-4" />
                                            Send reset instructions
                                        </>
                                    )}
                                </Button>
                            </form>
                        </>
                    ) : (
                        <div className="text-center py-8">
                            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-8 h-8 text-primary" />
                            </div>
                            <h1 className="font-heading font-bold text-2xl tracking-tight mb-2">
                                Check your email
                            </h1>
                            <p className="text-muted-foreground mb-6">
                                If an account exists for {email}, you'll receive password reset instructions shortly.
                            </p>
                            <p className="text-sm text-muted-foreground mb-8">
                                Didn't receive the email? Check your spam folder or{' '}
                                <button
                                    onClick={() => setSubmitted(false)}
                                    className="text-primary font-medium hover:underline"
                                >
                                    try again
                                </button>
                            </p>
                            <Link to="/login">
                                <Button variant="outline" className="rounded-full">
                                    <ArrowLeft className="mr-2 h-4 w-4" />
                                    Back to login
                                </Button>
                            </Link>
                        </div>
                    )}

                    <p className="text-center text-sm text-muted-foreground mt-8">
                        Remember your password?{' '}
                        <Link
                            to="/login"
                            className="text-primary font-medium hover:underline"
                        >
                            Sign in
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;
